/**
 * Helpers para a timeline de gráficos de telemetria (eixo X).
 * Compartilhados entre TelemetryCharts.js (tela) e EquipmentMonitorPrint.js (PDF).
 */

// Passos candidatos para ticks lineares do eixo X (ms).
export const TIME_TICK_STEPS = [
  60 * 1000,
  5 * 60 * 1000,
  10 * 60 * 1000,
  15 * 60 * 1000,
  30 * 60 * 1000,
  60 * 60 * 1000,
  2 * 60 * 60 * 1000,
  3 * 60 * 60 * 1000,
  6 * 60 * 60 * 1000,
  12 * 60 * 60 * 1000,
  24 * 60 * 60 * 1000,
  2 * 24 * 60 * 60 * 1000,
  7 * 24 * 60 * 60 * 1000,
  14 * 24 * 60 * 60 * 1000,
  30 * 24 * 60 * 60 * 1000,
];

// Ticks de tempo igualmente espaçados (linha do tempo linear) dentro de [minTs, maxTs].
export const buildTimeTicks = (minTs, maxTs, targetTicks = 6) => {
  if (!Number.isFinite(minTs) || !Number.isFinite(maxTs) || maxTs <= minTs) return null;
  const span = maxTs - minTs;
  const rawStep = span / targetTicks;
  const step = TIME_TICK_STEPS.find(candidate => candidate >= rawStep)
    || TIME_TICK_STEPS[TIME_TICK_STEPS.length - 1];
  const firstTick = Math.ceil(minTs / step) * step;
  const ticks = [];
  for (let tick = firstTick; tick <= maxTs; tick += step) {
    ticks.push(tick);
  }
  return ticks.length ? ticks : null;
};

// Offset (ms) do fuso em relação ao UTC para um dado instante (positivo = à frente do UTC).
export const getTzOffsetMs = (ts, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ts)).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  const asUTC = Date.UTC(
    +parts.year, +parts.month - 1, +parts.day,
    +parts.hour, +parts.minute, +parts.second
  );
  return asUTC - ts;
};

// Epoch (ms) da meia-noite local (00:00 no fuso) do dia que contém `ts`.
export const startOfLocalDay = (ts, timeZone) => {
  const off = getTzOffsetMs(ts, timeZone);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(ts)).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  return Date.UTC(+parts.year, +parts.month - 1, +parts.day, 0, 0, 0) - off;
};

// Lista de meia-noites locais dentro de [min, max] (robusto a DST via salto de 26h).
export const getLocalMidnights = (min, max, timeZone) => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
  const out = [];
  let m = startOfLocalDay(min, timeZone);
  if (m < min) m = startOfLocalDay(m + 26 * 60 * 60 * 1000, timeZone);
  let guard = 0;
  while (m <= max && guard < 400) {
    if (m >= min) out.push(m);
    m = startOfLocalDay(m + 26 * 60 * 60 * 1000, timeZone);
    guard += 1;
  }
  return out;
};

// "qua 27/05" — dia da semana abreviado (sem ponto) + dia/mês.
export const formatDayLabel = (ts, timeZone) => {
  const weekday = new Intl.DateTimeFormat('pt-BR', { timeZone, weekday: 'short' })
    .format(ts)
    .replace(/\.$/, '');
  const dayMonth = new Intl.DateTimeFormat('pt-BR', { timeZone, day: '2-digit', month: '2-digit' })
    .format(ts);
  return `${weekday} ${dayMonth}`;
};
